import * as Preferences from "./preference";
import { PreferenceElementWithCurrentValue, ConfigurationRegistry } from "./preferenceConfigurationConst";
import { PreferenceModel, PreferenceService, preferencesUpdatedEventName } from "../sdServices/preferenceSvc";
import { FlightingService } from "../sdServices/flightingSvc";
import { LocalizationService } from "../../ShellCommon/localization/localization";
import { WindowCommunicationBridgeService } from "../../ShellCommon/eventModule/window-communication-bridge";
import { LoggingService } from "../sdServices/loggingSvc";
import { UIService } from "./preferenceUISvc";
import { AudioService } from "../sdDirectives/audio-service";
import { DialogService } from "../sdServices/dialog";

/**
 * A type that includes stem as a variable attached to to $stateParams
 */
export type StateParamWithStem = ng.ui.IStateParamsService & {
  stem: string;
};
/**
 * Provides a consistent string for which to access preference loggingService items for both adding additional items and closing existing items
 */
// tslint:disable-next-line:variable-name
export const SD_PreferenceSaveMessage = "PrefItem";
/**
 * Provides a default value (also same as used by server) for Preference Section ID
 */
// tslint:disable-next-line:variable-name
export const SectionIdDefaultValue = "sectionId";

/**
 * SessionStorage key to store the state name of the parent who opened the preferences window.
 */
export const parentStateName = "parentStateName";

export type CurrentValueElementWithSection = {
  element: PreferenceElementWithCurrentValue;
  section: Preferences.SectionConfiguration;
};
const commConsoleAppId = "comm";
const serviceDeskAppId = "ServiceDesk";
export class PreferenceController {
  public sectionConfigurations: Preferences.SectionConfiguration[];
  private selectedSectionConfiguration: Preferences.SectionConfiguration;
  public activeStem: string;
  public changesAlertDisplayed: boolean;
  private preferencesFlightEnabled = false;
  public elementsLoaded = false;
  public progressError = false;
  private pendingChangesHeader: string;
  private preferencesLoaded = false;
  private pendingChangesMessage: string;
  // @ngInject
  constructor(
    private $scope: ng.IScope,
    private $stateParams: StateParamWithStem,
    private $state: ng.ui.IStateService,
    private $window: ng.IWindowService,
    private preferenceUIService: UIService,
    private preferenceService: PreferenceService,
    private loggingService: LoggingService,
    private audioService: AudioService,
    private dialogService: DialogService,
    private preferenceConfigurationRegistry: ConfigurationRegistry,
    private flightingService: FlightingService,
    private sdLocalizationService: LocalizationService,
    private $q: ng.IQService,
    private $timeout: ng.ITimeoutService,
    private windowCommunicationBridgeService: WindowCommunicationBridgeService
  ) {
    let flights = this.flightingService.getAllEnabledFlights();
    this.$scope.$evalAsync(() => {
      this.preferencesFlightEnabled = true;
      this.sectionConfigurations = this.preferenceConfigurationRegistry.getSectionConfigurations();
      this.localizeCommonStrings();
      this.localizeSectionConfigurations(this.sectionConfigurations);
      this.hideAppSpecificConfigs();
      this.hideNotFlightedConfigs(flights);

      this.selectedSectionConfiguration = this.initSelectedSectionConfiguration();
      this.activeStem = this.$stateParams.stem || this.selectedSectionConfiguration.stem;
      let allPromises: ng.IPromise<any>[] = [];
      for (let i = 0; i < this.sectionConfigurations.length; i++) {
        allPromises.push(
          this.preferenceUIService
            .getPreferenceElementsWithCurrentValue(this.sectionConfigurations[i].title)
            .then((preferenceElementsWithCurrentValue: PreferenceElementWithCurrentValue[]) => {
              this.completeElementLoading(this.sectionConfigurations[i], preferenceElementsWithCurrentValue, false);
            })
            .catch(() => {
              this.completeElementLoading(this.sectionConfigurations[i], undefined, true);
            })
        );
      }
      $q.all(allPromises).then(() => {
        this.preferencesLoaded = true;
        this.$window.document.title = "Settings - ServiceDesk";
      });
    });
  }

  public selectMenuChanged(prefrenceElement: PreferenceElementWithCurrentValue, selectedItem: any) {
    let element = <Preferences.PreferenceSelectMenu>prefrenceElement.configured;
    prefrenceElement.currentValue = selectedItem;
    if (element.selectMenuBehavior == Preferences.Sound) {
      this.audioService.play(prefrenceElement.currentValue.value);
    }
    this.checkForChanges();
  }

  public itemSelectionPredicate(option: any, selectedItem: any) {
    return selectedItem && option.value === selectedItem.value;
  }

  private completeElementLoading(
    sectionConfiguration: Preferences.SectionConfiguration,
    preferenceElementsWithCurrentValue: PreferenceElementWithCurrentValue[],
    isError: boolean
  ) {
    if (!isError) {
      this.localizePreferenceElements(preferenceElementsWithCurrentValue);
      sectionConfiguration.preferenceElements = preferenceElementsWithCurrentValue;
    }
    if (sectionConfiguration.title == this.selectedSectionConfiguration.title) {
      this.progressError = isError;
      this.elementsLoaded = true;
    }
  }

  private localizeCommonStrings() {
    this.pendingChangesHeader = this.sdLocalizationService.getLocalizedString(
      serviceDeskAppId,
      "_PREFERENCES_PENDING_CHANGES_ALERT_HEADER_"
    );
    this.pendingChangesMessage = this.sdLocalizationService.getLocalizedString(
      serviceDeskAppId,
      "_PREFERENCES_PENDING_CHANGES_ALERT_MESSAGE_"
    );
  }

  public localizePreferenceElements(elements: PreferenceElementWithCurrentValue[]) {
    elements.forEach((element) => {
      if (element.configured.descriptionLocKey) {
        element.configured.description = this.sdLocalizationService.getLocalizedString(
          serviceDeskAppId,
          element.configured.descriptionLocKey
        );
      }
      if (element.configured instanceof Preferences.PreferenceSelectMenu) {
        let selectMenuElement = <Preferences.PreferenceSelectMenu>element.configured;
        selectMenuElement.preferenceOptions.forEach((option) => {
          let name: string[] = [];
          option.locData.forEach((locData: Preferences.PreferenceLocModel) => {
            let locAppId = locData.locAppId ? locData.locAppId : serviceDeskAppId;
            name.push(this.sdLocalizationService.getLocalizedString(locAppId, locData.locKey));
          });
          option.name = name.join(" - ");
        });

        // sort selectMenu options
        if (selectMenuElement.sortOptions) {
          selectMenuElement.preferenceOptions = selectMenuElement.preferenceOptions.sort(
            (a: Preferences.PreferenceOption, b: Preferences.PreferenceOption) => {
              return a.name.localeCompare(b.name);
            }
          );
        }
      }
    });
  }

  public localizeSectionConfigurations(sectionConfigurations: Preferences.SectionConfiguration[]) {
    sectionConfigurations.forEach((sectionConfiguration) => {
      if (sectionConfiguration.titleLocKey) {
        sectionConfiguration.title = this.sdLocalizationService.getLocalizedString(
          serviceDeskAppId,
          sectionConfiguration.titleLocKey
        );
      } else if (!sectionConfiguration.title) {
        this.loggingService.logError(`Preferences localized title missing for ${sectionConfiguration.sectionId}`);
      }
    });
  }

  public commitChanges() {
    let preferenceModels: PreferenceModel[] = [];
    let changedElementsWithSections = this.checkForChanges();
    // appIdSectionIdPairs is an array of tuple [appId, sectionId]
    let appIdSectionIdPairs: [string, string][] = [];
    changedElementsWithSections.forEach((elementWithSection) => {
      let element = elementWithSection.element;
      let configured = <Preferences.InteractablePreferenceElement>element.configured;
      let section = elementWithSection.section;
      let value = element.currentValue.toString();
      appIdSectionIdPairs.push([section.appId, section.sectionId]);
      if (configured instanceof Preferences.PreferenceSelectMenu) {
        value = element.currentValue.value;
      }
      preferenceModels.push({
        appId: section.appId,
        sectionId: section.sectionId,
        content: { [configured.preferenceKey]: value }
      });
    });
    if (preferenceModels.length > 0) {
      this.preferenceService.setPreference(...preferenceModels).finally(() => {
        this.loggingService.closeUserVisibleMessage(SD_PreferenceSaveMessage);
        let notifyPromise = this.windowCommunicationBridgeService.publish(preferencesUpdatedEventName, {
          appIdSectionIdPairs: appIdSectionIdPairs
        });
        let onFinish = () => {
          this.$window.close();
        };
        notifyPromise.then(onFinish).catch(onFinish);
      });
    } else {
      this.$window.close();
    }
  }

  // TODO: Filtering should later be controlled by the user's rights.
  public hideAppSpecificConfigs() {
    let stateName = sessionStorage.getItem(parentStateName);
    if (stateName == commConsoleAppId) {
      this.sectionConfigurations = this.sectionConfigurations.filter((value) => {
        return value.appId.toLowerCase() == commConsoleAppId;
      });
    } else {
      this.sectionConfigurations = this.sectionConfigurations.filter((value) => {
        return value.appId.toLowerCase() != commConsoleAppId;
      });
    }
  }

  public hideNotFlightedConfigs(flights: string[]) {
    this.sectionConfigurations = this.sectionConfigurations.filter(
      (value) => !value.flightName || flights.includes(value.flightName)
    );
  }

  private initSelectedSectionConfiguration(): Preferences.SectionConfiguration {
    let sectionConfiguration: Preferences.SectionConfiguration = this.sectionConfigurations.filter(
      (sectionConfiguration: Preferences.SectionConfiguration) => {
        if (sectionConfiguration.stem && this.$stateParams.stem) {
          return sectionConfiguration.stem == this.$stateParams.stem;
        } else {
          return false;
        }
      }
    )[0];
    if (sectionConfiguration == undefined) {
      sectionConfiguration = this.sectionConfigurations[0];
    }
    return sectionConfiguration;
  }

  public getPivotUniqueIdentifier(sectionConfiguration: Preferences.SectionConfiguration): string {
    return sectionConfiguration.stem;
  }

  public checkForChanges(): CurrentValueElementWithSection[] {
    let changedElements: CurrentValueElementWithSection[] = [];
    this.sectionConfigurations.forEach((section) => {
      section.preferenceElements.forEach((currentElement: PreferenceElementWithCurrentValue) => {
        let configured = currentElement.configured;
        if (
          configured instanceof Preferences.InteractablePreferenceElement &&
          currentElement &&
          currentElement.currentValue
        ) {
          if (
            configured instanceof Preferences.PreferenceSelectMenu &&
            currentElement.currentValue.value != configured.initialValue.value
          ) {
            changedElements.push({ element: currentElement, section: section });
          } else if (
            !(currentElement.configured instanceof Preferences.PreferenceSelectMenu) &&
            currentElement.currentValue != configured.initialValue
          ) {
            changedElements.push({ element: currentElement, section: section });
          }
        }
      });
    });
    if (!this.changesAlertDisplayed && changedElements.length > 0) {
      this.changesAlertDisplayed = true;
      this.loggingService.logInfo("Preference changes exist", {
        displayText: this.pendingChangesMessage,
        uId: SD_PreferenceSaveMessage
      });
    } else if (this.changesAlertDisplayed && changedElements.length == 0) {
      this.changesAlertDisplayed = false;
      this.loggingService.closeUserVisibleMessage(SD_PreferenceSaveMessage);
    }
    return changedElements;
  }

  public deleteAll() {
    let deletePreferencesElement = document.getElementById("btnDeletePreferences");
    let confirmationContent = `<div class='preference-dialog'><p>{{'_PREFERENCES_GENERAL_SETTINGS_DELETE_CONFIRMATION_MESSAGE_'|loc:'ServiceDesk'}}</p>
            <button class='c-button confirm' ng-click='cancelPressed()'>Cancel</button>
            <button class='c-button confirm' type='submit' ng-click='confirmPressed()'>Confirm</button>
        </div>`;
    let successContent = `<div class='preference-dialog'><p>{{'_PREFERENCES_GENERAL_SETTINGS_DELETE_SUCCESS_MESSAGE_'|loc:'ServiceDesk'}}</p>
            <button class='c-button' ng-click='closePressed()'>Ok</button>
        </div>`;

    let dialogTitle = this.sdLocalizationService.getLocalizedString(
      serviceDeskAppId,
      "_PREFERENCES_GENERAL_SETTINGS_DELETE_TITLE_"
    );
    this.dialogService.showContent(
      confirmationContent,
      dialogTitle,
      {
        cancelPressed: () => {
          this.dialogService.hide();
        },
        confirmPressed: () => {
          this.preferenceService.deleteAllPreference().then((result: any) => {
            if (result) {
              this.dialogService.hide();
              this.dialogService.showContent(
                successContent,
                dialogTitle,
                {
                  closePressed: () => {
                    this.dialogService.hide();
                  }
                },
                false
              );
            } else {
              this.dialogService.hide();
              this.loggingService.logError(
                "Failed to delete all the preference.",
                "",
                "Failed to delete all the preference."
              );
            }
          });
        }
      },
      false
    );
  }
}
